// ============================================================================
// HoldCaseModal — case-level pause action
// ============================================================================
// Reason dropdown is filtered server-side. expected_resume_at is required
// when the chosen reason has requires_resume_date=true (e.g. negotiating,
// pending approval). Remarks are always mandatory.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import {
  useHoldCase,
  useHoldReasonOptions,
} from 'hooks/api/useCaseClosure';

export interface HoldCaseModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  onClose: () => void;
  onHeld: () => void;
}

const HoldCaseModal: React.FC<HoldCaseModalProps> = ({
  isOpen, caseId, caseCode, onClose, onHeld,
}) => {
  const { data: reasons, loading: reasonsLoading } = useHoldReasonOptions(isOpen ? caseId : null);
  const { hold, loading: saving } = useHoldCase();

  const [reasonId, setReasonId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [resumeAt, setResumeAt] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setReasonId('');
    setRemarks('');
    setResumeAt('');
    setFieldErrors({});
  }, [isOpen]);

  const selectedReason = useMemo(
    () => reasons.find((r) => r.id === reasonId),
    [reasons, reasonId],
  );

  const reasonOptions = useMemo(
    () => reasons.map((r) => ({ value: r.id, label: r.holdName })),
    [reasons],
  );

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!reasonId) errors.reasonId = 'Select a hold reason';
    if (!remarks.trim()) errors.remarks = 'Remarks are required';
    if (selectedReason?.requiresResumeDate && !resumeAt) {
      errors.resumeAt = `${selectedReason.holdName} requires an expected resume date`;
    }
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setConfirmOpen(true);
  }, [reasonId, remarks, resumeAt, selectedReason]);

  const handleConfirm = useCallback(async () => {
    setConfirmOpen(false);
    try {
      await hold(caseId, {
        holdReasonId: reasonId,
        remarks: remarks.trim(),
        expectedResumeAt: resumeAt || null,
      });
      notificationService.toast.success(`Case ${caseCode} put on hold.`);
      onHeld();
      onClose();
    } catch (err) {
      notificationService.toast.error(
        err instanceof Error ? err.message : 'Failed to hold case.',
      );
    }
  }, [hold, caseId, caseCode, reasonId, remarks, resumeAt, onHeld, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Hold Case — {caseCode}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group" data-field="reasonId">
            <SyutSelect
              label="Hold Reason"
              required
              options={reasonOptions}
              value={reasonId}
              onChange={(v) => setReasonId((v as string) ?? '')}
              error={fieldErrors.reasonId}
              isLoading={reasonsLoading}
              placeholder={reasonsLoading ? 'Loading…' : 'Select reason'}
            />
          </div>

          <div className="form-group" data-field="remarks">
            <label className="form-label">Remarks <span className="text-danger">*</span></label>
            <textarea
              className={`form-textarea${fieldErrors.remarks ? ' form-field-error' : ''}`}
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Why is the case being paused?"
            />
            {fieldErrors.remarks && <div className="form-error-message">{fieldErrors.remarks}</div>}
          </div>

          <div className="form-group" data-field="resumeAt">
            <label className="form-label">
              Expected Resume Date
              {selectedReason?.requiresResumeDate && <span className="text-danger"> *</span>}
            </label>
            <input
              type="date"
              className={`form-input${fieldErrors.resumeAt ? ' form-field-error' : ''}`}
              value={resumeAt}
              onChange={(e) => setResumeAt(e.target.value)}
            />
            {fieldErrors.resumeAt && <div className="form-error-message">{fieldErrors.resumeAt}</div>}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Holding…' : 'Hold Case'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Hold"
        message={`Put case ${caseCode} on hold? Workflow transitions will be refused until the case is resumed.`}
        confirmLabel="Hold"
        cancelLabel="Cancel"
        variant="default"
        loading={saving}
        loadingMessage="Holding…"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default HoldCaseModal;
